<script setup>
import { ref, computed, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';

import { useAdminStore } from '@/store/AdminStore';

const adminStore = useAdminStore();
const toast = useToast();
const confirm = useConfirm();

const users = computed(() => adminStore.users);
const isLoading = computed(() => adminStore.isLoading);

// Advanced filter state
const searchQuery = ref('');
const statusFilter = ref(null);
const planFilter = ref(null);
const roleFilter = ref(null);

const filters = ref({
  global: { value: '', matchMode: 'contains' },
});

// Filtered users with advanced filters
const filteredUsers = computed(() => {
  let result = users.value || [];

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      (u) =>
        u.email?.toLowerCase().includes(query) ||
        u.first_name?.toLowerCase().includes(query) ||
        u.last_name?.toLowerCase().includes(query),
    );
  }

  if (statusFilter.value !== null) {
    result = result.filter((u) => u.is_active === statusFilter.value);
  }

  if (planFilter.value) {
    result = result.filter((u) => u.plan === planFilter.value);
  }

  if (roleFilter.value) {
    result = result.filter((u) => u.roles?.includes(roleFilter.value));
  }

  return result;
});

const activeFiltersCount = computed(() => {
  let count = 0;
  if (searchQuery.value) count++;
  if (statusFilter.value !== null) count++;
  if (planFilter.value) count++;
  if (roleFilter.value) count++;
  return count;
});

const clearFilters = () => {
  searchQuery.value = '';
  statusFilter.value = null;
  planFilter.value = null;
  roleFilter.value = null;
};

// Stats computed
const userStats = computed(() => {
  const all = users.value || [];
  return {
    total: all.length,
    active: all.filter((u) => u.is_active).length,
    admins: all.filter((u) => u.roles?.includes('ROLE_ADMIN')).length,
    pro: all.filter((u) => u.plan === 'pro' || u.plan === 'enterprise').length,
  };
});

// Dialog state
const showDialog = ref(false);
const formData = ref({
  id: null,
  email: '',
  first_name: '',
  last_name: '',
  roles: [],
  plan: 'free',
  is_active: true,
});

const planOptions = [
  { label: 'Free', value: 'free', icon: 'pi pi-user' },
  { label: 'Basic', value: 'basic', icon: 'pi pi-star' },
  { label: 'Pro', value: 'pro', icon: 'pi pi-star-fill' },
  { label: 'Enterprise', value: 'enterprise', icon: 'pi pi-building' },
];

const roleOptions = [
  { label: 'User', value: 'ROLE_USER' },
  { label: 'Admin', value: 'ROLE_ADMIN' },
];

const statusOptions = [
  { label: 'All Status', value: null },
  { label: 'Active', value: true },
  { label: 'Inactive', value: false },
];

const resetForm = () => {
  formData.value = {
    id: null,
    email: '',
    first_name: '',
    last_name: '',
    roles: [],
    plan: 'free',
    is_active: true,
  };
};

const openEditDialog = (user) => {
  formData.value = {
    ...user,
    roles: user.roles ? [...user.roles] : [],
  };
  showDialog.value = true;
};

const saveUser = async () => {
  try {
    await adminStore.updateUser(formData.value.id, formData.value);
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'User updated successfully',
      life: 3000,
    });
    showDialog.value = false;
    resetForm();
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update user',
      life: 3000,
    });
  }
};

const confirmDelete = (user) => {
  confirm.require({
    message: `Are you sure you want to delete user "${user.email}"? This cannot be undone.`,
    header: 'Delete User',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        await adminStore.deleteUser(user.id);
        toast.add({
          severity: 'success',
          summary: 'Deleted',
          detail: 'User deleted successfully',
          life: 3000,
        });
      } catch {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete user',
          life: 3000,
        });
      }
    },
  });
};

const toggleUserStatus = async (user) => {
  try {
    await adminStore.updateUser(user.id, { is_active: !user.is_active });
    toast.add({
      severity: 'success',
      summary: 'Status Updated',
      detail: `User ${user.is_active ? 'deactivated' : 'activated'}`,
      life: 3000,
    });
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update status',
      life: 3000,
    });
  }
};

const toggleRole = (role) => {
  const index = formData.value.roles.indexOf(role);
  if (index === -1) {
    formData.value.roles.push(role);
  } else {
    formData.value.roles.splice(index, 1);
  }
};

const hasRole = (role) => {
  return formData.value.roles.includes(role);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getInitials = (user) => {
  const first = user.first_name?.[0] || '';
  const last = user.last_name?.[0] || '';
  return (first + last).toUpperCase() || user.email?.[0]?.toUpperCase() || '?';
};

const getPlanClass = (plan) => {
  return `plan-badge plan-${plan}`;
};

onMounted(() => {
  adminStore.fetchUsers(true);
});
</script>

<template>
  <div class="users-manager">
    <ConfirmDialog />

    <!-- Header Section -->
    <div class="manager-header">
      <div class="header-content">
        <div class="header-icon">
          <i class="pi pi-users"></i>
        </div>
        <div class="header-text">
          <h2>User Management</h2>
          <p>View and manage user accounts, roles, and permissions</p>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="stats-row">
        <div class="mini-stat">
          <span class="stat-value">{{ userStats.total }}</span>
          <span class="stat-label">Total Users</span>
        </div>
        <div class="mini-stat active">
          <span class="stat-value">{{ userStats.active }}</span>
          <span class="stat-label">Active</span>
        </div>
        <div class="mini-stat admin">
          <span class="stat-value">{{ userStats.admins }}</span>
          <span class="stat-label">Admins</span>
        </div>
        <div class="mini-stat pro">
          <span class="stat-value">{{ userStats.pro }}</span>
          <span class="stat-label">Pro/Enterprise</span>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="filters-section">
      <div class="search-box">
        <i class="pi pi-search"></i>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search by name or email..."
          class="search-input"
        />
        <button v-if="searchQuery" class="clear-search" @click="searchQuery = ''">
          <i class="pi pi-times"></i>
        </button>
      </div>

      <div class="filter-group">
        <Dropdown
          v-model="statusFilter"
          :options="statusOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Status"
          class="filter-dropdown"
        />
        <Dropdown
          v-model="planFilter"
          :options="[{ label: 'All Plans', value: null }, ...planOptions]"
          optionLabel="label"
          optionValue="value"
          placeholder="Plan"
          class="filter-dropdown"
        />
        <Dropdown
          v-model="roleFilter"
          :options="[
            { label: 'All Roles', value: null },
            { label: 'Admin', value: 'ROLE_ADMIN' },
            { label: 'User', value: 'ROLE_USER' },
          ]"
          optionLabel="label"
          optionValue="value"
          placeholder="Role"
          class="filter-dropdown"
        />

        <button v-if="activeFiltersCount > 0" class="clear-filters-btn" @click="clearFilters">
          <i class="pi pi-filter-slash"></i>
          Clear ({{ activeFiltersCount }})
        </button>
      </div>
    </div>

    <!-- Users Table -->
    <div class="table-container">
      <DataTable
        :value="filteredUsers"
        :loading="isLoading"
        :filters="filters"
        responsiveLayout="scroll"
        class="users-table"
        paginator
        :rows="10"
        :rowsPerPageOptions="[5, 10, 25, 50]"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      >
        <Column field="email" header="User" sortable style="min-width: 280px">
          <template #body="{ data }">
            <div class="user-cell">
              <div class="user-avatar" :class="{ admin: data.roles?.includes('ROLE_ADMIN') }">
                {{ getInitials(data) }}
              </div>
              <div class="user-info">
                <span class="user-name">
                  {{ data.first_name }} {{ data.last_name }}
                  <span v-if="data.roles?.includes('ROLE_ADMIN')" class="admin-badge">
                    <i class="pi pi-shield"></i> Admin
                  </span>
                </span>
                <span class="user-email">{{ data.email }}</span>
              </div>
            </div>
          </template>
        </Column>

        <Column field="plan" header="Plan" sortable style="min-width: 120px">
          <template #body="{ data }">
            <span :class="getPlanClass(data.plan)">
              {{ data.plan }}
            </span>
          </template>
        </Column>

        <Column field="created_at" header="Joined" sortable style="min-width: 130px">
          <template #body="{ data }">
            <span class="date-cell">{{ formatDate(data.created_at) }}</span>
          </template>
        </Column>

        <Column field="is_active" header="Status" sortable style="min-width: 120px">
          <template #body="{ data }">
            <button
              class="status-toggle"
              :class="{ active: data.is_active }"
              @click="toggleUserStatus(data)"
              v-tooltip.top="data.is_active ? 'Click to deactivate' : 'Click to activate'"
            >
              <span class="status-dot"></span>
              {{ data.is_active ? 'Active' : 'Inactive' }}
            </button>
          </template>
        </Column>

        <Column header="Actions" style="width: 120px">
          <template #body="{ data }">
            <div class="action-buttons">
              <button class="action-btn edit" v-tooltip.top="'Edit User'" @click="openEditDialog(data)">
                <i class="pi pi-pencil"></i>
              </button>
              <button class="action-btn delete" v-tooltip.top="'Delete User'" @click="confirmDelete(data)">
                <i class="pi pi-trash"></i>
              </button>
            </div>
          </template>
        </Column>

        <template #empty>
          <div class="empty-state">
            <div class="empty-icon">
              <i class="pi pi-users"></i>
            </div>
            <h3>No users found</h3>
            <p>Try adjusting your search or filter criteria</p>
            <button v-if="activeFiltersCount > 0" class="reset-btn" @click="clearFilters">
              <i class="pi pi-refresh"></i> Reset Filters
            </button>
          </div>
        </template>
      </DataTable>
    </div>

    <!-- Edit Dialog -->
    <Dialog
      v-model:visible="showDialog"
      :style="{ width: '520px' }"
      modal
      :closable="true"
      class="user-dialog"
    >
      <template #header>
        <div class="dialog-header">
          <div class="dialog-icon">
            <i class="pi pi-user-edit"></i>
          </div>
          <div>
            <h3>Edit User</h3>
            <p>{{ formData.email }}</p>
          </div>
        </div>
      </template>

      <div class="dialog-form">
        <div class="form-row">
          <div class="form-field">
            <label>First Name</label>
            <InputText v-model="formData.first_name" placeholder="Enter first name" />
          </div>
          <div class="form-field">
            <label>Last Name</label>
            <InputText v-model="formData.last_name" placeholder="Enter last name" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-field">
            <label>Plan</label>
            <Dropdown
              v-model="formData.plan"
              :options="planOptions"
              optionLabel="label"
              optionValue="value"
              class="w-100"
            />
          </div>
          <div class="form-field">
            <label>Status</label>
            <Dropdown
              v-model="formData.is_active"
              :options="[
                { label: 'Active', value: true },
                { label: 'Inactive', value: false },
              ]"
              optionLabel="label"
              optionValue="value"
              class="w-100"
            />
          </div>
        </div>

        <div class="form-field">
          <label>Roles</label>
          <div class="roles-grid">
            <button
              v-for="role in roleOptions"
              :key="role.value"
              type="button"
              class="role-toggle"
              :class="{ active: hasRole(role.value), admin: role.value === 'ROLE_ADMIN' }"
              @click="toggleRole(role.value)"
            >
              <i :class="role.value === 'ROLE_ADMIN' ? 'pi pi-shield' : 'pi pi-user'"></i>
              {{ role.label }}
              <i v-if="hasRole(role.value)" class="pi pi-check check-icon"></i>
            </button>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <Button label="Cancel" class="cancel-btn" @click="showDialog = false" />
          <Button label="Save Changes" icon="pi pi-check" class="save-btn" @click="saveUser" />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
.users-manager {
  animation: fadeIn 0.4s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header */
.manager-header {
  margin-bottom: 1.5rem;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.header-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  box-shadow: 0 8px 32px rgba(0, 212, 255, 0.3);
}

.header-text h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--calid-text-primary, #fff);
  margin: 0;
}

.header-text p {
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0.25rem 0 0;
  font-size: 0.9rem;
}

/* Stats Row */
.stats-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.mini-stat {
  flex: 1;
  min-width: 120px;
  padding: 1rem 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  text-align: center;
  transition: all 0.3s ease;
}

.mini-stat:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(0, 212, 255, 0.3);
}

.mini-stat .stat-value {
  display: block;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--calid-text-primary, #fff);
}

.mini-stat .stat-label {
  font-size: 0.75rem;
  color: var(--calid-text-secondary, #94a3b8);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.mini-stat.active .stat-value {
  color: #10b981;
}

.mini-stat.admin .stat-value {
  color: #f59e0b;
}

.mini-stat.pro .stat-value {
  color: #8b5cf6;
}

/* Filters */
.filters-section {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 250px;
}

.search-box i.pi-search {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--calid-text-secondary, #94a3b8);
  font-size: 0.9rem;
}

.search-input {
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 2.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: var(--calid-text-primary, #fff);
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #00d4ff;
  box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.1);
}

.search-input::placeholder {
  color: var(--calid-text-secondary, #64748b);
}

.clear-search {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--calid-text-secondary, #94a3b8);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: color 0.2s;
}

.clear-search:hover {
  color: #ef4444;
}

.filter-group {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
}

.filter-dropdown {
  min-width: 130px;
}

:deep(.filter-dropdown .p-dropdown) {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}

.clear-filters-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #ef4444;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-filters-btn:hover {
  background: rgba(239, 68, 68, 0.2);
}

/* Table Container */
.table-container {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  overflow: hidden;
}

:deep(.users-table) {
  background: transparent;
}

:deep(.users-table .p-datatable-thead > tr > th) {
  background: rgba(255, 255, 255, 0.03);
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--calid-text-secondary, #94a3b8);
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 1rem;
}

:deep(.users-table .p-datatable-tbody > tr) {
  background: transparent;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  transition: background 0.2s;
}

:deep(.users-table .p-datatable-tbody > tr:hover) {
  background: rgba(255, 255, 255, 0.03);
}

:deep(.users-table .p-datatable-tbody > tr > td) {
  border: none;
  padding: 1rem;
  color: var(--calid-text-primary, #fff);
}

/* User Cell */
.user-cell {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}

.user-avatar {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--calid-text-primary, #fff);
  border: 2px solid rgba(255, 255, 255, 0.1);
}

.user-avatar.admin {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-color: rgba(245, 158, 11, 0.5);
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.user-name {
  font-weight: 600;
  color: var(--calid-text-primary, #fff);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.user-email {
  font-size: 0.8rem;
  color: var(--calid-text-secondary, #94a3b8);
}

.admin-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.2) 100%);
  border: 1px solid rgba(245, 158, 11, 0.4);
  border-radius: 6px;
  font-size: 0.65rem;
  font-weight: 600;
  color: #fbbf24;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Plan Badge */
.plan-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.plan-free {
  background: rgba(100, 116, 139, 0.2);
  color: #94a3b8;
  border: 1px solid rgba(100, 116, 139, 0.3);
}

.plan-basic {
  background: rgba(59, 130, 246, 0.15);
  color: #60a5fa;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.plan-pro {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%);
  color: #a78bfa;
  border: 1px solid rgba(139, 92, 246, 0.4);
}

.plan-enterprise {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.2) 100%);
  color: #fbbf24;
  border: 1px solid rgba(245, 158, 11, 0.4);
}

/* Date Cell */
.date-cell {
  color: var(--calid-text-secondary, #94a3b8);
  font-size: 0.85rem;
}

/* Status Toggle */
.status-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;
}

.status-toggle.active {
  background: rgba(16, 185, 129, 0.15);
  border-color: rgba(16, 185, 129, 0.4);
  color: #10b981;
}

.status-toggle:not(.active) {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #f87171;
}

.status-toggle:hover {
  transform: scale(1.02);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  animation: pulse-dot 2s infinite;
}

@keyframes pulse-dot {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  color: var(--calid-text-secondary, #94a3b8);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-btn:hover {
  transform: translateY(-2px);
}

.action-btn.edit:hover {
  background: rgba(0, 212, 255, 0.1);
  border-color: rgba(0, 212, 255, 0.4);
  color: #00d4ff;
}

.action-btn.delete:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.4);
  color: #ef4444;
}

/* Empty State */
.empty-state {
  padding: 4rem 2rem;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.03);
  border: 2px dashed rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon i {
  font-size: 2rem;
  color: var(--calid-text-secondary, #64748b);
}

.empty-state h3 {
  font-size: 1.25rem;
  color: var(--calid-text-primary, #fff);
  margin: 0 0 0.5rem;
}

.empty-state p {
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0 0 1.5rem;
}

.reset-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%);
  border: none;
  border-radius: 10px;
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.reset-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 212, 255, 0.3);
}

/* Dialog Styles */
:deep(.user-dialog .p-dialog) {
  background: var(--calid-bg-secondary, #111827);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

:deep(.user-dialog .p-dialog-header) {
  background: transparent;
  border: none;
  padding: 1.5rem 1.5rem 0;
}

:deep(.user-dialog .p-dialog-content) {
  background: transparent;
  padding: 1.5rem;
}

:deep(.user-dialog .p-dialog-footer) {
  background: transparent;
  border: none;
  padding: 0 1.5rem 1.5rem;
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.dialog-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: white;
}

.dialog-header h3 {
  font-size: 1.25rem;
  color: var(--calid-text-primary, #fff);
  margin: 0;
}

.dialog-header p {
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0;
  font-size: 0.85rem;
}

/* Dialog Form */
.dialog-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-field label {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--calid-text-secondary, #94a3b8);
}

:deep(.dialog-form .p-inputtext) {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  color: var(--calid-text-primary, #fff);
}

:deep(.dialog-form .p-inputtext:focus) {
  border-color: #00d4ff;
  box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.1);
}

:deep(.dialog-form .p-dropdown) {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}

/* Roles Grid */
.roles-grid {
  display: flex;
  gap: 0.75rem;
}

.role-toggle {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: var(--calid-text-secondary, #94a3b8);
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.role-toggle:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.2);
}

.role-toggle.active {
  background: rgba(0, 212, 255, 0.1);
  border-color: rgba(0, 212, 255, 0.4);
  color: #00d4ff;
}

.role-toggle.active.admin {
  background: rgba(245, 158, 11, 0.15);
  border-color: rgba(245, 158, 11, 0.4);
  color: #fbbf24;
}

.check-icon {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  font-size: 0.7rem;
}

/* Dialog Footer */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.cancel-btn {
  padding: 0.75rem 1.25rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: var(--calid-text-secondary, #94a3b8);
}

.cancel-btn:hover {
  background: rgba(255, 255, 255, 0.05);
}

.save-btn {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%);
  border: none;
  border-radius: 10px;
  color: white;
}

.save-btn:hover {
  box-shadow: 0 8px 24px rgba(0, 212, 255, 0.3);
}

/* Paginator Styling */
:deep(.p-paginator) {
  background: rgba(255, 255, 255, 0.02);
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding: 1rem;
}

:deep(.p-paginator .p-paginator-pages .p-paginator-page) {
  min-width: 36px;
  height: 36px;
  border-radius: 8px;
  margin: 0 0.125rem;
}

:deep(.p-paginator .p-paginator-pages .p-paginator-page.p-highlight) {
  background: linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%);
  border: none;
  color: white;
}

/* Responsive */
@media (max-width: 768px) {
  .stats-row {
    gap: 0.75rem;
  }

  .mini-stat {
    min-width: calc(50% - 0.5rem);
  }

  .filters-section {
    flex-direction: column;
  }

  .search-box {
    min-width: 100%;
  }

  .filter-group {
    width: 100%;
    justify-content: flex-start;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
